/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { getPostLoginPath } from "auth/utils";
import { DialogBox } from "layouts/brands/DialogBox";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

export const SearchContext = createContext({
  searchKeyword: "",
  setSearchKeyword: () => {},
});

export const SearchProvider = ({ children }) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const location = useLocation();

  useEffect(() => {
    setSearchKeyword("");
  }, [location.pathname]);
  return (
    <SearchContext.Provider value={{ searchKeyword, setSearchKeyword }}>
      {children}
    </SearchContext.Provider>
  );
};

export const FilterContext = createContext({
  selectedBrands: null,
  setSelectedBrands: () => {},
});

export const FilterProvider = ({ children }) => {
  const [selectedBrands, setSelectedBrands] = useState(null);

  return (
    <FilterContext.Provider value={{ selectedBrands, setSelectedBrands }}>
      {children}
    </FilterContext.Provider>
  );
};

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    color: "info",
    isVisible: false,
    message: "",
  });

  useEffect(() => {
    if (notification?.isVisible) {
      const timer = setTimeout(() => {
        setNotification((prevNotification) => ({
          ...prevNotification,
          isVisible: false,
          message: "",
        }));
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <NotificationContext.Provider value={{ notification, setNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  return useContext(NotificationContext);
};

// authentication context
export const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {},
  user: null,
});
const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const navigate = useNavigate();

  const user = useMemo(() => {
    const userDetails = localStorage.getItem("userDetails");
    try {
      return userDetails ? JSON.parse(userDetails) : null;
    } catch (error) {
      console.error(error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setIsAuthenticated(true);
  }, []);

  const login = (response) => {
    localStorage.setItem("token", response.access_token);
    localStorage.setItem("refreshToken", response.refresh_token);
    localStorage.setItem("userDetails", JSON.stringify(response.userDetails));
    setIsAuthenticated(true);
    navigate(getPostLoginPath(response.userDetails));
  };

  const logout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    navigate("/auth/login");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "BroadwayBrandContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: true,
    sidenavColor: "secondary",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node,
};

// Context module functions
const setMiniSidenav = (dispatch, value) =>
  dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) =>
  dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) =>
  dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) =>
  dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) =>
  dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) =>
  dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) =>
  dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) =>
  dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

const FormDirtyContext = createContext();
const FormDirtyProvider = ({ children }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState('/');
  const routeRef = useRef(route);
  const companyFormSubmitRef = useRef();
  const navigate = useNavigate();
  const [handleFormSave, setHandleFormSave] = useState(() => () => {
    console.log('Default action executed');
    console.log(companyFormSubmitRef)
  });
  
  const handleDontSave = () => {
    setOpen(false);
    navigate(routeRef.current);
    setIsDirty(false)
  };
  
  const handleSave = () => {
    setOpen(false);
    let saved = handleFormSave();
    saved && setIsDirty(false);
    saved && navigate(routeRef.current);
  };
  return (
    <FormDirtyContext.Provider value={{ isDirty, setIsDirty, open, setOpen, routeRef, setHandleFormSave }}>
      {children}
      <DialogBox status={open} setStatus={setOpen} title={"Alert"} description={"There are unsaved data, do you want to save the changes"} handleDontSave={handleDontSave} handleSave={handleSave} />
    </FormDirtyContext.Provider>
  );
};
const useFormDirtyContext = () => useContext(FormDirtyContext);



export {
  AuthContextProvider,
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  FormDirtyProvider,
  useFormDirtyContext
};
